import {
  useMemo,
  useState,
  useCallback,
  useEffect,
  useRef,
  type TouchEvent,
} from "react";
import {
  format,
  getDaysInMonth,
  startOfMonth,
  addMonths,
  isWeekend,
  isSameDay,
  parseISO,
  isAfter,
  isBefore,
  endOfMonth,
  min,
  max,
  setMonth,
  setYear,
} from "date-fns";
import { de } from "date-fns/locale";
import { ChevronLeft, ChevronRight, Users, List } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ProjectWithRelations } from "@/hooks/useProjects";
import { PersonWithCompany } from "@/hooks/usePeople";
import { EmployeeAbsence } from "@/hooks/useEmployeeAbsences";
import {
  EmployeeFunctionAssignment,
  EmployeeBookedAvailability,
} from "@/hooks/usePersonBookings";
import { cn } from "@/lib/utils";
import { CalendarProjectCell } from "./CalendarProjectCell";
import { CalendarDragSelection } from "./CalendarDragSelection";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface CalendarGridProps {
  projects: ProjectWithRelations[];
  onProjectClick?: (project: ProjectWithRelations) => void;
  onCreateProject?: (startDate: Date, endDate: Date) => void;
  headerActions?: React.ReactNode;
  employees?: PersonWithCompany[];
  absences?: EmployeeAbsence[];
  bookings?: EmployeeFunctionAssignment[];
  bookedAvailability?: EmployeeBookedAvailability[];
  projectIdsWithFunctions?: string[];
  /** null = hidden, string[] = selected person IDs */
  visiblePeople?: string[] | null;
  onVisiblePeopleChange?: (people: string[] | null) => void;
}

const MONTHS_TO_SHOW_DESKTOP = 6;
const MONTHS_TO_SHOW_MOBILE = 1;
const MONTH_RENDER_BUFFER = 3;
const MOBILE_SWIPE_THRESHOLD = 60;
const ROW_HEIGHT = 20;
const VISIBLE_LANES = 4;
const LANE_WIDTH = 60;
const DAY_NUM_WIDTH = 24;
const DAY_ABBREV_WIDTH = 24;
const PERSON_COL_WIDTH = 14;
const BASE_MONTH_WIDTH =
  DAY_NUM_WIDTH + DAY_ABBREV_WIDTH + VISIBLE_LANES * LANE_WIDTH + 10; // day number + abbreviation + event lanes + padding
const HEADER_HEIGHT = 36;

// German holidays (simplified - major ones)
const getHolidays = (year: number): Date[] => {
  return [
    new Date(year, 0, 1), // Neujahr
    new Date(year, 4, 1), // Tag der Arbeit
    new Date(year, 9, 3), // Tag der Deutschen Einheit
    new Date(year, 11, 25), // Weihnachten
    new Date(year, 11, 26), // 2. Weihnachten
  ];
};

const getDayAbbrev = (date: Date): string => {
  return format(date, "EEEEEE", { locale: de });
};

const ABSENCE_COLORS: Record<string, string> = {
  vacation: "bg-blue-400 dark:bg-blue-600",
  sick: "bg-red-400 dark:bg-red-600",
  unpaid: "bg-yellow-400 dark:bg-yellow-600",
  other: "bg-gray-400 dark:bg-gray-500",
};

const BOOKING_COLOR = "bg-green-400 dark:bg-green-600";

const formatAbsenceTooltip = (absence: {
  type: string;
  notes: string | null;
}) => {
  if (absence.type === "other" && absence.notes) return absence.notes;
  return absence.notes ? `${absence.type}: ${absence.notes}` : absence.type;
};

interface ProjectSpan {
  project: ProjectWithRelations;
  startDay: number;
  endDay: number;
  isStart: boolean;
  isEnd: boolean;
  lane: number;
  eventStartDay: number | null;
  eventEndDay: number | null;
  isEventStart: boolean;
  isEventEnd: boolean;
}

interface DragState {
  isActive: boolean;
  startMonthIdx: number | null;
  startDay: number | null;
  currentMonthIdx: number | null;
  currentDay: number | null;
}

const MONTH_NAMES = [
  "Januar",
  "Februar",
  "März",
  "April",
  "Mai",
  "Juni",
  "Juli",
  "August",
  "September",
  "Oktober",
  "November",
  "Dezember",
];

const LegendSwatch = ({
  className,
  overlayClassName,
}: {
  className: string;
  overlayClassName?: string;
}) => (
  <span className="relative inline-block h-2.5 w-2.5 overflow-hidden rounded-sm">
    <span className={cn("absolute inset-0", className)} />
    {overlayClassName && (
      <span
        className={cn(
          "absolute inset-y-0 left-1/4 right-1/4",
          overlayClassName,
        )}
      />
    )}
  </span>
);

export function CalendarGrid({
  projects,
  onProjectClick,
  onCreateProject,
  headerActions,
  employees,
  absences,
  bookings,
  bookedAvailability,
  projectIdsWithFunctions,
  visiblePeople,
  onVisiblePeopleChange,
}: CalendarGridProps) {
  const isMobile = useIsMobile();
  const monthsToShow = isMobile
    ? MONTHS_TO_SHOW_MOBILE
    : MONTHS_TO_SHOW_DESKTOP;
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const [startDate, setStartDate] = useState(() => startOfMonth(new Date()));
  const [renderedMonthCount, setRenderedMonthCount] = useState(
    () => monthsToShow,
  );
  const [visibleMonthRange, setVisibleMonthRange] = useState(() => ({
    startIdx: 0,
    endIdx: monthsToShow - 1,
  }));
  const [lanePages, setLanePages] = useState<Record<string, number>>({});
  const [dragState, setDragState] = useState<DragState>({
    isActive: false,
    startMonthIdx: null,
    startDay: null,
    currentMonthIdx: null,
    currentDay: null,
  });

  const months = useMemo(() => {
    return Array.from({ length: renderedMonthCount }, (_, i) =>
      addMonths(startDate, i),
    );
  }, [startDate, renderedMonthCount]);
  const visibleStartMonth = useMemo(
    () => addMonths(startDate, visibleMonthRange.startIdx),
    [startDate, visibleMonthRange.startIdx],
  );
  const visibleEndMonth = useMemo(
    () => addMonths(startDate, visibleMonthRange.endIdx),
    [startDate, visibleMonthRange.endIdx],
  );

  const holidays = useMemo(() => {
    const years = new Set(months.map((month) => month.getFullYear()));
    return Array.from(years).flatMap((year) => getHolidays(year));
  }, [months]);

  const isHoliday = (date: Date) => {
    return holidays.some((h) => isSameDay(h, date));
  };

  const getMonthKey = (month: Date) => format(month, "yyyy-MM");

  // Compute which people to show
  const sortedEmployees = useMemo(() => {
    if (!employees) return [];
    return [...employees].sort((a, b) =>
      `${a.first_name} ${a.last_name}`.localeCompare(
        `${b.first_name} ${b.last_name}`,
      ),
    );
  }, [employees]);

  const shownPeople = useMemo(() => {
    if (!visiblePeople || !sortedEmployees.length) return [];
    return sortedEmployees.filter((e) => visiblePeople.includes(e.id));
  }, [visiblePeople, sortedEmployees]);

  // Build absence lookup: personId -> date -> { type, notes }
  const absenceLookup = useMemo(() => {
    if (!absences)
      return new Map<
        string,
        Map<string, { type: string; notes: string | null }>
      >();
    const lookup = new Map<
      string,
      Map<string, { type: string; notes: string | null }>
    >();
    for (const a of absences) {
      if (a.absence_type === "sick") continue;
      let personMap = lookup.get(a.person_id);
      if (!personMap) {
        personMap = new Map();
        lookup.set(a.person_id, personMap);
      }
      const d = new Date(a.start_date);
      const end = new Date(a.end_date);
      while (d <= end) {
        const dateStr = d.toISOString().slice(0, 10);
        personMap.set(dateStr, {
          type: a.absence_type,
          notes: a.notes ?? null,
        });
        d.setDate(d.getDate() + 1);
      }
    }
    return lookup;
  }, [absences]);

  // Build booking lookup: personId -> Map of date strings -> project names[]
  // Combines function assignments with booked availability (for projects without functions)
  const bookingLookup = useMemo(() => {
    const lookup = new Map<string, Map<string, string[]>>();

    // 1. Function assignment bookings
    if (bookings) {
      for (const b of bookings) {
        if (!b.project_function) continue;
        let personMap = lookup.get(b.person_id);
        if (!personMap) {
          personMap = new Map();
          lookup.set(b.person_id, personMap);
        }
        const dateStr = b.project_function.function_date;
        const projectLabel = b.project_function.project?.number
          ? `${b.project_function.project.number} ${b.project_function.project.name}`
          : (b.project_function.project?.name ?? "Booking");
        const existing = personMap.get(dateStr);
        if (existing) {
          if (!existing.includes(projectLabel)) existing.push(projectLabel);
        } else {
          personMap.set(dateStr, [projectLabel]);
        }
      }
    }

    // 2. Booked availability fallback for projects WITHOUT functions
    if (bookedAvailability) {
      const projectsWithFuncs = new Set(projectIdsWithFunctions ?? []);
      for (const ba of bookedAvailability) {
        if (projectsWithFuncs.has(ba.project_id)) continue;

        let personMap = lookup.get(ba.person_id);
        if (!personMap) {
          personMap = new Map();
          lookup.set(ba.person_id, personMap);
        }

        const projectLabel = ba.project?.number
          ? `${ba.project.number} ${ba.project.name}`
          : (ba.project?.name ?? "Booking");

        for (const dateStr of ba.available_days ?? []) {
          const existing = personMap.get(dateStr);
          if (existing) {
            if (!existing.includes(projectLabel)) existing.push(projectLabel);
          } else {
            personMap.set(dateStr, [projectLabel]);
          }
        }
      }
    }

    return lookup;
  }, [bookings, bookedAvailability, projectIdsWithFunctions]);

  const personColCount = shownPeople.length;
  const monthWidth = BASE_MONTH_WIDTH + personColCount * PERSON_COL_WIDTH;
  const gridHeaderHeight = isMobile ? 0 : HEADER_HEIGHT;
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);

  // Toggle helpers
  const showPeople = visiblePeople !== null && visiblePeople.length > 0;

  const handleToggleAll = (checked: boolean) => {
    if (checked) {
      onVisiblePeopleChange?.(sortedEmployees.map((e) => e.id));
    } else {
      onVisiblePeopleChange?.(null);
    }
  };

  const handleTogglePerson = (personId: string, checked: boolean) => {
    const current = visiblePeople ?? [];
    if (checked) {
      onVisiblePeopleChange?.([...current, personId]);
    } else {
      const next = current.filter((id) => id !== personId);
      onVisiblePeopleChange?.(next.length > 0 ? next : null);
    }
  };

  // Get project spans for a specific month with lane assignments
  const getProjectSpansForMonth = useCallback(
    (month: Date): ProjectSpan[] => {
      const monthStart = startOfMonth(month);
      const monthEnd = endOfMonth(month);
      const spans: Omit<ProjectSpan, "lane">[] = [];

      projects.forEach((project) => {
        const eventStartStr =
          project.event_start_date || project.event_end_date;
        const eventEndStr = project.event_end_date || project.event_start_date;
        const planningStartStr =
          project.planning_start_date || eventStartStr || null;
        const planningEndStr = project.planning_end_date || eventEndStr || null;

        // Bar range: planning dates if set, else event dates
        const hasPlanningDates =
          project.planning_start_date || project.planning_end_date;
        const barStartStr = hasPlanningDates ? planningStartStr : eventStartStr;
        const barEndStr = hasPlanningDates ? planningEndStr : eventEndStr;

        if (!barStartStr && !barEndStr) return;

        const barStart = barStartStr ? parseISO(barStartStr) : null;
        const barEnd = barEndStr ? parseISO(barEndStr) : null;

        let effectiveStart: Date | null = null;
        let effectiveEnd: Date | null = null;

        if (barStart && barEnd) {
          if (isAfter(barStart, monthEnd) || isBefore(barEnd, monthStart)) {
            return;
          }
          effectiveStart = max([barStart, monthStart]);
          effectiveEnd = min([barEnd, monthEnd]);
        } else if (barStart && !barEnd) {
          if (
            barStart.getMonth() !== month.getMonth() ||
            barStart.getFullYear() !== month.getFullYear()
          ) {
            return;
          }
          effectiveStart = barStart;
          effectiveEnd = barStart;
        } else if (!barStart && barEnd) {
          if (
            barEnd.getMonth() !== month.getMonth() ||
            barEnd.getFullYear() !== month.getFullYear()
          ) {
            return;
          }
          effectiveStart = barEnd;
          effectiveEnd = barEnd;
        }

        if (!effectiveStart || !effectiveEnd) return;

        const startDay = effectiveStart.getDate();
        const endDay = effectiveEnd.getDate();
        const isStart = barStart ? isSameDay(effectiveStart, barStart) : true;
        const isEnd = barEnd ? isSameDay(effectiveEnd, barEnd) : true;

        // Compute event sub-range within this month (only when planning dates are set AND event dates exist)
        let eventStartDay: number | null = null;
        let eventEndDay: number | null = null;
        let isEventStart = false;
        let isEventEnd = false;

        if (hasPlanningDates && eventStartStr && eventEndStr) {
          const evStart = parseISO(eventStartStr);
          const evEnd = parseISO(eventEndStr);

          if (!isAfter(evStart, monthEnd) && !isBefore(evEnd, monthStart)) {
            const clampedEvStart = max([evStart, monthStart]);
            const clampedEvEnd = min([evEnd, monthEnd]);
            eventStartDay = clampedEvStart.getDate();
            eventEndDay = clampedEvEnd.getDate();
            isEventStart = isSameDay(clampedEvStart, evStart);
            isEventEnd = isSameDay(clampedEvEnd, evEnd);
          }
        }

        spans.push({
          project,
          startDay,
          endDay,
          isStart,
          isEnd,
          eventStartDay,
          eventEndDay,
          isEventStart,
          isEventEnd,
        });
      });

      // Sort by start day, then by length (longer first)
      spans.sort((a, b) => {
        if (a.startDay !== b.startDay) return a.startDay - b.startDay;
        return b.endDay - b.startDay - (a.endDay - a.startDay);
      });

      // Assign as many logical lanes as needed; rendering pages those lanes.
      const laneOccupancy: number[][] = [];

      const assignedSpans: ProjectSpan[] = spans.map((span) => {
        let assignedLane = 0;
        for (let lane = 0; lane <= laneOccupancy.length; lane++) {
          const occupiedDays = laneOccupancy[lane] ?? [];
          const isLaneFree = !occupiedDays.some(
            (occupiedDay) =>
              occupiedDay >= span.startDay && occupiedDay <= span.endDay,
          );
          if (isLaneFree) {
            assignedLane = lane;
            break;
          }
        }

        laneOccupancy[assignedLane] ??= [];
        for (let day = span.startDay; day <= span.endDay; day++) {
          laneOccupancy[assignedLane].push(day);
        }

        return { ...span, lane: assignedLane };
      });

      return assignedSpans;
    },
    [projects],
  );

  const monthSpans = useMemo(() => {
    return months.map((month) => getProjectSpansForMonth(month));
  }, [months, getProjectSpansForMonth]);

  useEffect(() => {
    setRenderedMonthCount(
      isMobile ? monthsToShow : monthsToShow + MONTH_RENDER_BUFFER,
    );
    setVisibleMonthRange({ startIdx: 0, endIdx: monthsToShow - 1 });
  }, [isMobile, monthsToShow, startDate]);

  useEffect(() => {
    if (isMobile) return;

    const viewport = scrollAreaRef.current?.querySelector<HTMLElement>(
      "[data-radix-scroll-area-viewport]",
    );
    if (!viewport) return;

    viewport.scrollLeft = 0;
  }, [isMobile, startDate]);

  useEffect(() => {
    if (isMobile) return;

    const viewport = scrollAreaRef.current?.querySelector<HTMLElement>(
      "[data-radix-scroll-area-viewport]",
    );
    if (!viewport) return;

    const handleScroll = () => {
      const columnWidth = isMobile ? viewport.clientWidth : monthWidth;
      if (columnWidth <= 0) return;
      const visibleStartIdx = Math.max(
        0,
        Math.floor(viewport.scrollLeft / columnWidth),
      );
      const visibleEndIdx = Math.max(
        visibleStartIdx,
        Math.ceil((viewport.scrollLeft + viewport.clientWidth) / columnWidth) -
          1,
      );
      setVisibleMonthRange((range) =>
        range.startIdx === visibleStartIdx && range.endIdx === visibleEndIdx
          ? range
          : { startIdx: visibleStartIdx, endIdx: visibleEndIdx },
      );
      const visibleEndMonthCount = Math.ceil(
        (viewport.scrollLeft + viewport.clientWidth) / columnWidth,
      );
      const nextRenderedMonthCount = Math.max(
        monthsToShow + MONTH_RENDER_BUFFER,
        visibleEndMonthCount + MONTH_RENDER_BUFFER,
      );
      setRenderedMonthCount((count) =>
        nextRenderedMonthCount > count ? nextRenderedMonthCount : count,
      );
    };

    viewport.addEventListener("scroll", handleScroll, { passive: true });
    return () => viewport.removeEventListener("scroll", handleScroll);
  }, [isMobile, monthWidth, monthsToShow]);

  const handlePrevious = () => {
    if (!isMobile) {
      const viewport = scrollAreaRef.current?.querySelector<HTMLElement>(
        "[data-radix-scroll-area-viewport]",
      );

      if (viewport && visibleMonthRange.startIdx > 0) {
        viewport.scrollTo({
          left: Math.max(0, viewport.scrollLeft - monthWidth),
          behavior: "smooth",
        });
        return;
      }
    }

    setStartDate((prev) => addMonths(prev, -1));
  };

  const handleNext = () => {
    setStartDate((prev) => addMonths(prev, 1));
  };

  const handleTouchStart = (event: TouchEvent<HTMLDivElement>) => {
    if (!isMobile) return;
    const touch = event.touches[0];
    touchStartRef.current = { x: touch.clientX, y: touch.clientY };
  };

  const handleTouchEnd = (event: TouchEvent<HTMLDivElement>) => {
    if (!isMobile || !touchStartRef.current) return;
    const touch = event.changedTouches[0];
    const deltaX = touch.clientX - touchStartRef.current.x;
    const deltaY = touch.clientY - touchStartRef.current.y;
    touchStartRef.current = null;

    if (
      Math.abs(deltaX) < MOBILE_SWIPE_THRESHOLD ||
      Math.abs(deltaX) <= Math.abs(deltaY) * 1.5
    ) {
      return;
    }

    if (deltaX < 0) {
      handleNext();
    } else {
      handlePrevious();
    }
  };

  const handleLanePageChange = (monthKey: string, nextPage: number) => {
    setLanePages((prev) => ({ ...prev, [monthKey]: nextPage }));
  };

  const handleMonthChange = (monthIndex: string) => {
    setStartDate((prev) => setMonth(prev, parseInt(monthIndex)));
  };

  const handleYearChange = (year: string) => {
    setStartDate((prev) => setYear(prev, parseInt(year)));
  };

  const handleToday = () => {
    setStartDate(startOfMonth(new Date()));
  };

  // Drag handlers
  const handleDragStart = useCallback(
    (monthIdx: number, day: number) => {
      if (!onCreateProject) return;
      setDragState({
        isActive: true,
        startMonthIdx: monthIdx,
        startDay: day,
        currentMonthIdx: monthIdx,
        currentDay: day,
      });
    },
    [onCreateProject],
  );

  const handleDragMove = useCallback(
    (monthIdx: number, day: number) => {
      if (!dragState.isActive) return;
      setDragState((prev) => ({
        ...prev,
        currentMonthIdx: monthIdx,
        currentDay: day,
      }));
    },
    [dragState.isActive],
  );

  const handleDragEnd = useCallback(() => {
    if (
      !dragState.isActive ||
      dragState.startMonthIdx === null ||
      dragState.startDay === null ||
      dragState.currentMonthIdx === null ||
      dragState.currentDay === null
    ) {
      setDragState({
        isActive: false,
        startMonthIdx: null,
        startDay: null,
        currentMonthIdx: null,
        currentDay: null,
      });
      return;
    }

    const startMonth = months[dragState.startMonthIdx];
    const endMonth = months[dragState.currentMonthIdx];

    const date1 = new Date(
      startMonth.getFullYear(),
      startMonth.getMonth(),
      dragState.startDay,
    );
    const date2 = new Date(
      endMonth.getFullYear(),
      endMonth.getMonth(),
      dragState.currentDay,
    );

    const [startDate, endDate] =
      date1 <= date2 ? [date1, date2] : [date2, date1];

    onCreateProject?.(startDate, endDate);

    setDragState({
      isActive: false,
      startMonthIdx: null,
      startDay: null,
      currentMonthIdx: null,
      currentDay: null,
    });
  }, [dragState, months, onCreateProject]);

  // Get selection range for a specific month
  const getSelectionForMonth = (monthIdx: number) => {
    if (
      !dragState.isActive ||
      dragState.startMonthIdx === null ||
      dragState.startDay === null ||
      dragState.currentMonthIdx === null ||
      dragState.currentDay === null
    ) {
      return null;
    }

    const startIdx = Math.min(
      dragState.startMonthIdx,
      dragState.currentMonthIdx,
    );
    const endIdx = Math.max(dragState.startMonthIdx, dragState.currentMonthIdx);

    if (monthIdx < startIdx || monthIdx > endIdx) return null;

    const startMonth = months[dragState.startMonthIdx];
    const endMonth = months[dragState.currentMonthIdx];
    const currentMonth = months[monthIdx];

    const date1 = new Date(
      startMonth.getFullYear(),
      startMonth.getMonth(),
      dragState.startDay,
    );
    const date2 = new Date(
      endMonth.getFullYear(),
      endMonth.getMonth(),
      dragState.currentDay,
    );
    const [selStart, selEnd] = date1 <= date2 ? [date1, date2] : [date2, date1];

    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);

    const effectiveStart = max([selStart, monthStart]);
    const effectiveEnd = min([selEnd, monthEnd]);

    if (effectiveStart > effectiveEnd) return null;

    return {
      startDay: effectiveStart.getDate(),
      endDay: effectiveEnd.getDate(),
    };
  };

  const maxDays = 31;

  const legendContent = (
    <div className="space-y-3 text-xs">
      {showPeople && (
        <div className="space-y-2">
          <div className="font-medium">People</div>
          <div className="grid gap-1.5">
            <div className="flex items-center gap-2">
              <LegendSwatch className="bg-blue-400 dark:bg-blue-600" />
              <span>Vacation</span>
            </div>
            <div className="flex items-center gap-2">
              <LegendSwatch className="bg-yellow-400 dark:bg-yellow-600" />
              <span>Unpaid</span>
            </div>
            <div className="flex items-center gap-2">
              <LegendSwatch className="bg-gray-400 dark:bg-gray-500" />
              <span>Other</span>
            </div>
            <div className="flex items-center gap-2">
              <LegendSwatch className="bg-green-400 dark:bg-green-600" />
              <span>Booked</span>
            </div>
          </div>
        </div>
      )}
      <div className="space-y-2">
        <div className="font-medium">Projects</div>
        <div className="grid gap-1.5">
          <div className="flex items-center gap-2">
            <LegendSwatch className="bg-[hsl(var(--type-live))]" />
            <span>Confirmed live</span>
          </div>
          <div className="flex items-center gap-2">
            <LegendSwatch className="bg-[hsl(var(--type-film))]" />
            <span>Confirmed film</span>
          </div>
          <div className="flex items-center gap-2">
            <LegendSwatch className="bg-[hsl(var(--type-gfx))]" />
            <span>Confirmed gfx / tracking</span>
          </div>
          <div className="flex items-center gap-2">
            <LegendSwatch className="bg-[hsl(var(--type-consultancy))]" />
            <span>Confirmed consultancy</span>
          </div>
          <div className="flex items-center gap-2">
            <LegendSwatch className="bg-[hsl(var(--status-offer))]" />
            <span>Offer</span>
          </div>
          <div className="flex items-center gap-2">
            <LegendSwatch className="bg-[hsl(var(--status-initial))]" />
            <span>Initial contact</span>
          </div>
          <div className="flex items-center gap-2">
            <LegendSwatch
              className="bg-[hsl(var(--type-live))]/60"
              overlayClassName="bg-[hsl(var(--type-live))]"
            />
            <span>Planning range with event dates inside</span>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <TooltipProvider delayDuration={0} skipDelayDuration={0}>
      <div className="flex flex-col h-full">
        {/* Navigation */}
        <div className="flex items-center justify-between mb-4 px-2">
          {isMobile ? (
            <>
              <Button variant="outline" size="icon" onClick={handlePrevious}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <div className="flex items-center gap-2">
                <Select
                  value={startDate.getMonth().toString()}
                  onValueChange={handleMonthChange}
                >
                  <SelectTrigger className="w-[110px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MONTH_NAMES.map((name, index) => (
                      <SelectItem key={index} value={index.toString()}>
                        {name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={startDate.getFullYear().toString()}
                  onValueChange={handleYearChange}
                >
                  <SelectTrigger className="w-[80px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 5 }, (_, i) => {
                      const year = new Date().getFullYear() - 2 + i;
                      return (
                        <SelectItem key={year} value={year.toString()}>
                          {year}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
              <Button variant="outline" size="icon" onClick={handleNext}>
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm">
                    <List className="h-4 w-4 mr-1" />
                    Legend
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-64 p-3" align="end">
                  {legendContent}
                </PopoverContent>
              </Popover>
            </>
          ) : (
            <>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={handlePrevious}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={handleToday}>
                  Today
                </Button>
                {employees && employees.length > 0 && (
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant={showPeople ? "default" : "outline"}
                        size="sm"
                      >
                        <Users className="h-4 w-4 mr-1" />
                        People
                        {showPeople && (
                          <span className="ml-1 text-xs">
                            ({shownPeople.length})
                          </span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-56 p-3" align="start">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 pb-2 border-b">
                          <Checkbox
                            id="absence-all"
                            checked={
                              visiblePeople !== null &&
                              visiblePeople.length === sortedEmployees.length
                            }
                            onCheckedChange={(checked) =>
                              handleToggleAll(checked === true)
                            }
                          />
                          <label
                            htmlFor="absence-all"
                            className="text-sm font-medium cursor-pointer"
                          >
                            All
                          </label>
                        </div>
                        <div className="max-h-64 overflow-y-auto space-y-1">
                          {sortedEmployees.map((emp) => (
                            <div
                              key={emp.id}
                              className="flex items-center gap-2"
                            >
                              <Checkbox
                                id={`absence-${emp.id}`}
                                checked={
                                  visiblePeople?.includes(emp.id) ?? false
                                }
                                onCheckedChange={(checked) =>
                                  handleTogglePerson(emp.id, checked === true)
                                }
                              />
                              <label
                                htmlFor={`absence-${emp.id}`}
                                className="text-sm cursor-pointer truncate"
                              >
                                {emp.first_name} {emp.last_name}
                              </label>
                            </div>
                          ))}
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                )}
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm">
                      <List className="h-4 w-4 mr-1" />
                      Legend
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-64 p-3" align="start">
                    {legendContent}
                  </PopoverContent>
                </Popover>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  {format(visibleStartMonth, "yyyy")}
                  {visibleStartMonth.getFullYear() !==
                    visibleEndMonth.getFullYear() &&
                    ` - ${format(visibleEndMonth, "yyyy")}`}
                </span>
                {headerActions}
              </div>
            </>
          )}
        </div>

        {/* Calendar Grid - Column-based layout */}
        <ScrollArea
          ref={scrollAreaRef}
          className="flex-1 [&>[data-radix-scroll-area-viewport]]:!overflow-y-visible"
        >
          <div
            className="flex select-none"
            style={
              isMobile
                ? undefined
                : {
                    minWidth: monthWidth * renderedMonthCount,
                  }
            }
            onMouseLeave={() => {
              if (dragState.isActive) {
                handleDragEnd();
              }
            }}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            {/* Month columns */}
            {months.map((month, monthIdx) => {
              const daysInMonth = getDaysInMonth(month);
              const spans = monthSpans[monthIdx];
              const selection = getSelectionForMonth(monthIdx);
              const monthKey = getMonthKey(month);
              const laneCount =
                spans.length > 0
                  ? Math.max(...spans.map((span) => span.lane)) + 1
                  : 0;
              const totalLanePages = Math.max(
                1,
                Math.ceil(laneCount / VISIBLE_LANES),
              );
              const lanePage = Math.min(
                lanePages[monthKey] ?? 0,
                totalLanePages - 1,
              );
              const visibleLaneStart = lanePage * VISIBLE_LANES;
              const visibleLaneEnd = visibleLaneStart + VISIBLE_LANES;
              const visibleSpans = spans.filter(
                (span) =>
                  span.lane >= visibleLaneStart && span.lane < visibleLaneEnd,
              );

              return (
                <div
                  key={monthIdx}
                  className={cn(
                    "border-l border-border relative",
                    isMobile ? "w-full shrink-0" : "shrink-0",
                  )}
                  style={isMobile ? undefined : { width: monthWidth }}
                >
                  {!isMobile && (
                    <div
                      className="pl-2 py-2 font-semibold text-sm bg-muted/50 border-b border-border sticky top-0 z-10 flex items-center"
                      style={{ height: HEADER_HEIGHT }}
                    >
                      <span>{format(month, "MMMM", { locale: de })}</span>
                      {totalLanePages > 1 && (
                        <div className="ml-2 flex items-center gap-1 text-[10px] font-medium text-muted-foreground">
                          <button
                            type="button"
                            className="flex h-5 w-5 items-center justify-center rounded border border-border bg-background hover:bg-accent disabled:opacity-40"
                            disabled={lanePage === 0}
                            onClick={() =>
                              handleLanePageChange(monthKey, lanePage - 1)
                            }
                            aria-label={`Show previous events for ${format(month, "MMMM yyyy", { locale: de })}`}
                          >
                            <ChevronLeft className="h-3 w-3" />
                          </button>
                          <span className="tabular-nums">
                            {lanePage + 1}/{totalLanePages}
                          </span>
                          <button
                            type="button"
                            className="flex h-5 w-5 items-center justify-center rounded border border-border bg-background hover:bg-accent disabled:opacity-40"
                            disabled={lanePage >= totalLanePages - 1}
                            onClick={() =>
                              handleLanePageChange(monthKey, lanePage + 1)
                            }
                            aria-label={`Show more events for ${format(month, "MMMM yyyy", { locale: de })}`}
                          >
                            <ChevronRight className="h-3 w-3" />
                          </button>
                        </div>
                      )}
                      {/* Person initials in header */}
                      {showPeople && (
                        <div className="flex ml-auto">
                          {shownPeople.map((person) => (
                            <Tooltip key={person.id}>
                              <TooltipTrigger asChild>
                                <span
                                  className="text-[9px] text-muted-foreground flex items-center justify-center shrink-0 border-l border-border/30"
                                  style={{ width: PERSON_COL_WIDTH }}
                                >
                                  {person.first_name[0]}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="text-xs">
                                {person.first_name} {person.last_name}
                              </TooltipContent>
                            </Tooltip>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Day cells background */}
                  {Array.from({ length: maxDays }, (_, dayIndex) => {
                    const dayNum = dayIndex + 1;
                    const isValidDay = dayNum <= daysInMonth;
                    const currentDate = new Date(
                      month.getFullYear(),
                      month.getMonth(),
                      dayNum,
                    );
                    const isWeekendDay = isValidDay && isWeekend(currentDate);
                    const isHolidayDay = isValidDay && isHoliday(currentDate);
                    const dateStr = isValidDay
                      ? `${month.getFullYear()}-${String(month.getMonth() + 1).padStart(2, "0")}-${String(dayNum).padStart(2, "0")}`
                      : "";

                    return (
                      <div
                        key={dayNum}
                        style={{ height: ROW_HEIGHT }}
                        className={cn(
                          "flex border-b border-border/50",
                          !isValidDay && "bg-muted/30",
                          isWeekendDay && "bg-muted/60",
                          isHolidayDay && "bg-destructive/10",
                          isValidDay && onCreateProject && "cursor-crosshair",
                        )}
                        onMouseDown={(e) => {
                          if (isValidDay && e.button === 0) {
                            e.preventDefault();
                            handleDragStart(monthIdx, dayNum);
                          }
                        }}
                        onMouseEnter={() => {
                          if (isValidDay && dragState.isActive) {
                            handleDragMove(monthIdx, dayNum);
                          }
                        }}
                        onMouseUp={() => {
                          if (dragState.isActive) {
                            handleDragEnd();
                          }
                        }}
                      >
                        {isValidDay && (
                          <span
                            style={{ width: DAY_NUM_WIDTH }}
                            className="shrink-0 flex items-center justify-center text-xs font-medium text-muted-foreground"
                          >
                            {dayNum}
                          </span>
                        )}
                        {isValidDay && (
                          <span
                            style={{ width: DAY_ABBREV_WIDTH }}
                            className={cn(
                              "shrink-0 flex items-center justify-center text-xs",
                              isWeekendDay && "text-muted-foreground",
                              isHolidayDay && "text-destructive",
                            )}
                          >
                            {getDayAbbrev(currentDate)}
                          </span>
                        )}
                        {/* Spacer for project lanes */}
                        <span className="flex-1" />
                        {/* Person columns (absences + bookings) */}
                        {showPeople &&
                          isValidDay &&
                          shownPeople.map((person) => {
                            const personAbsences = absenceLookup.get(person.id);
                            const absence = personAbsences?.get(dateStr);
                            const personBookings = bookingLookup.get(person.id);
                            const bookingLabels = personBookings?.get(dateStr);
                            const cellColor = absence
                              ? ABSENCE_COLORS[absence.type] ||
                                ABSENCE_COLORS.other
                              : bookingLabels
                                ? BOOKING_COLOR
                                : "";
                            const hasTooltip = !!(absence || bookingLabels);
                            return (
                              <Tooltip key={person.id}>
                                <TooltipTrigger asChild>
                                  <span
                                    className={cn(
                                      "shrink-0 flex items-center justify-center border-l border-border/30",
                                      cellColor,
                                    )}
                                    style={{ width: PERSON_COL_WIDTH }}
                                  />
                                </TooltipTrigger>
                                {hasTooltip && (
                                  <TooltipContent
                                    side="top"
                                    className="text-xs"
                                  >
                                    <div>
                                      <span className="font-medium">
                                        {person.first_name} {person.last_name}
                                      </span>
                                      {absence && (
                                        <div>
                                          {formatAbsenceTooltip(absence)}
                                        </div>
                                      )}
                                      {bookingLabels?.map((label) => (
                                        <div key={label}>{label}</div>
                                      ))}
                                    </div>
                                  </TooltipContent>
                                )}
                              </Tooltip>
                            );
                          })}
                        {showPeople && !isValidDay && (
                          <span
                            style={{
                              width: shownPeople.length * PERSON_COL_WIDTH,
                            }}
                          />
                        )}
                      </div>
                    );
                  })}

                  {/* Selection overlay */}
                  {selection && (
                    <CalendarDragSelection
                      startDay={selection.startDay}
                      endDay={selection.endDay}
                      headerHeight={gridHeaderHeight}
                      rowHeight={ROW_HEIGHT}
                      width={
                        isMobile
                          ? `calc(100% - ${DAY_NUM_WIDTH + DAY_ABBREV_WIDTH + 8 + personColCount * PERSON_COL_WIDTH}px)`
                          : BASE_MONTH_WIDTH -
                            DAY_NUM_WIDTH -
                            DAY_ABBREV_WIDTH -
                            8
                      }
                      leftOffset={DAY_NUM_WIDTH + DAY_ABBREV_WIDTH + 4}
                    />
                  )}

                  {/* Project spans - absolutely positioned */}
                  {visibleSpans.map((span) => {
                    const spanDays = span.endDay - span.startDay + 1;
                    const topOffset =
                      gridHeaderHeight + (span.startDay - 1) * ROW_HEIGHT;
                    const height = spanDays * ROW_HEIGHT - 4; // -4 for visual gap
                    const visibleLane = span.lane - visibleLaneStart;
                    const laneWidthPercent = (100 - 4) / VISIBLE_LANES; // 4% for margins
                    const leftOffsetPercent =
                      2 + visibleLane * laneWidthPercent;

                    return (
                      <CalendarProjectCell
                        key={span.project.id}
                        project={span.project}
                        onClick={() => onProjectClick?.(span.project)}
                        isStart={span.isStart}
                        isEnd={span.isEnd}
                        spanHeight={height}
                        leftOffset={
                          isMobile
                            ? `calc(${DAY_NUM_WIDTH + DAY_ABBREV_WIDTH}px + ${leftOffsetPercent}%)`
                            : DAY_NUM_WIDTH +
                              DAY_ABBREV_WIDTH +
                              4 +
                              visibleLane * LANE_WIDTH
                        }
                        topOffset={topOffset}
                        width={
                          isMobile ? `${laneWidthPercent - 1}%` : LANE_WIDTH - 4
                        }
                        barStartDay={span.startDay}
                        eventStartDay={span.eventStartDay}
                        eventEndDay={span.eventEndDay}
                        isEventStart={span.isEventStart}
                        isEventEnd={span.isEventEnd}
                        rowHeight={ROW_HEIGHT}
                      />
                    );
                  })}
                </div>
              );
            })}
          </div>
          <ScrollBar orientation="horizontal" />
        </ScrollArea>
      </div>
    </TooltipProvider>
  );
}
